import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import NoticeBoard from "@/components/NoticeBoard";
import { Beaker, Users, BookMarked, Database, ScrollText, LogOut, Backpack, Trophy, ShoppingBag, Map, MapPinned, ShieldCheck, GraduationCap, Sliders } from "lucide-react";

const NAV = [
  { to: "/", end: true, label: "Tableau de bord", icon: Beaker, testid: "nav-dashboard" },
  { to: "/dresseurs", label: "Dresseurs", icon: Users, testid: "nav-trainers" },
  { to: "/carte", label: "Carte", icon: MapPinned, testid: "nav-carte" },
  { to: "/pokedex", label: "Pokédex", icon: BookMarked, testid: "nav-pokedex" },
  { to: "/grade", label: "Grade", icon: GraduationCap, testid: "nav-grade" },
  { to: "/carnet", label: "Carnet", icon: Map, testid: "nav-carnet" },
  { to: "/pc", label: "PC du labo", icon: Database, testid: "nav-pc" },
  { to: "/inventaire", label: "Inventaire", icon: Backpack, testid: "nav-inventory" },
  { to: "/boutique", label: "Boutique", icon: ShoppingBag, testid: "nav-shop" },
  { to: "/collegiales", label: "Collégiales", icon: Trophy, testid: "nav-collegiales" },
  { to: "/journal", label: "Journal", icon: ScrollText, testid: "nav-journal" },
];

const ADMIN_NAV = [
  { to: "/admin/routes", label: "Admin · Routes", icon: ShieldCheck, testid: "nav-admin-routes" },
  { to: "/admin/tuning", label: "Admin · Équilibrage", icon: Sliders, testid: "nav-admin-tuning" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!user?.is_admin;
  const navItems = isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;

  // Phase 1 — Regional accent color follows the professor's active region.
  useEffect(() => {
    const region = user?.region || "kanto";
    document.body.setAttribute("data-region", region);
    return () => document.body.removeAttribute("data-region");
  }, [user?.region]);

  const doLogout = async () => {
    await logout();
    nav("/auth");
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      <NoticeBoard/>
      {/* Top header */}
      <header className="border-b border-[#2B2B2B] bg-[#FCF9F2] px-3 md:px-6 py-2 md:py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <div className="stamp shrink-0" data-testid="header-stamp">PROF. {user?.professor_name?.toUpperCase()}</div>
          <div className="hidden md:block">
            <div className="overline">Collégiale de Bourg-Palette · {user?.region?.toUpperCase()}</div>
            <h1 className="font-serif-display text-2xl leading-none mt-1">Laboratoire de Recherche</h1>
          </div>
          {user?.grade && (
            <NavLink
              to="/grade"
              data-testid="header-grade-badge"
              className="hidden sm:inline-flex items-center gap-1 border border-[#2E4F32] bg-[#F0F5E7] text-[#2E4F32] px-2 py-1 font-mono text-[10px] uppercase tracking-wider hover:bg-[#E4F0D2]"
              title={`Grade ${user.grade.id} — ${user.grade.title}`}
            >
              <GraduationCap size={11}/>
              {user.grade.title}
            </NavLink>
          )}
        </div>
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <span className="font-mono text-xs text-[#4A4A4A] hidden sm:inline truncate max-w-[200px]" data-testid="header-email">{user?.email}</span>
          <button data-testid="logout-btn" className="btn-ink text-[10px] md:text-xs px-2 md:px-3" onClick={doLogout}>
            <LogOut size={12}/> <span className="hidden sm:inline">Quitter</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Left nav */}
        <aside className="w-[220px] border-r border-[#2B2B2B] bg-[#F4EFE6] p-3 hidden md:block">
          <nav className="flex flex-col gap-1">
            {navItems.map(({ to, end, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase tracking-wider transition-colors ${
                    isActive
                      ? "text-[#FCF9F2] border-[color:var(--region-accent-ink)]"
                      : "text-[#1A1A1A] border-transparent hover:bg-[#EAE4D9]"
                  }`
                }
                style={({ isActive }) => (isActive ? { background: "var(--region-accent)" } : undefined)}
              >
                <Icon size={14}/>
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-6 panel p-3 border-l-4" style={{ borderLeftColor: "var(--region-accent)" }}>
            <div className="overline">Région active</div>
            <div className="font-serif-display text-2xl leading-none mt-1 capitalize">{user?.region || "kanto"}</div>
            <div className="text-xs font-mono text-[#4A4A4A] mt-2">
              {user?.region === "johto" ? "16 arènes · 251 espèces" : "8 arènes · 151 espèces"}
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 overflow-auto p-4 md:p-6 pb-24 md:pb-6 relative">
          <div className="grain absolute inset-0 pointer-events-none" />
          <div className="relative z-10">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile floating bottom nav (horizontally scrollable when too many items) */}
      <nav
        className="md:hidden fixed bottom-3 left-3 right-3 border border-[#1A1A1A] z-40 overflow-x-auto no-scrollbar"
        style={{
          background: "rgba(252, 249, 242, 0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 6px 0 0 rgba(26,26,26,0.85), 0 0 0 1px rgba(26,26,26,0.1)",
          scrollSnapType: "x mandatory",
        }}
        data-testid="mobile-bottom-nav"
      >
        <div className="flex min-w-max">
          {navItems.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `shrink-0 w-[64px] flex flex-col items-center py-2 text-[9px] font-mono uppercase border-r border-[#2B2B2B] last:border-r-0 ${
                  isActive ? "text-[#FCF9F2]" : "text-[#1A1A1A]"
                }`
              }
              style={({ isActive }) => ({
                scrollSnapAlign: "start",
                background: isActive ? "var(--region-accent)" : undefined,
              })}
            >
              <Icon size={16}/>
              <span className="mt-0.5 truncate max-w-full px-1">{label.split(" ")[0]}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
